import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createIntegration,
  deleteFBLead,
  deleteIntegration,
  disConnectFacebook,
  getAllFacebookBusinesses,
  getAllFacebookPages,
  getAllIntegrations,
  getAllStudentLeads,
  getFormFieldsByFromID,
  getIntegrationByID,
  getLeadFormByPageID,
  getPagesByBusinessID,
  updateIntegration,
  updatePageSubscription,
} from "./services/integration.services";
import { ILeadIntegrationDetails, ILeadState } from "./integrationModel";
import { ErrorToast } from "../../components/toast/Toasts";

const initialState: ILeadState = {
  loading: false,
  businessLoading: false,
  error: "",
  IntegrationID: null,
  FBLeadID: null,
  FBBusinessList: [],
  FBPageList: [],
  LeadFormList: [],
  FormFieldsList: [],
  LeadIntegrationList: [],
  LeadIntegrationDetails: {
    Title: "",
    FormID: "",
    PageID: "",
    FileName: "",
    MobileNo: "",
    EmailID: "",
  },
  StudentLeadList: [],
};

const integrationSlice = createSlice({
  name: "integration",
  initialState,
  reducers: {
    setIntegrationID: (state, action) => {
      state.IntegrationID = action.payload;
    },
    clearIntegrationID: (state) => {
      state.IntegrationID = initialState.IntegrationID;
    },
    setFBLeadID: (state, action) => {
      state.FBLeadID = action.payload;
    },
    clearFBLeadID: (state) => {
      state.FBLeadID = initialState.FBLeadID;
    },
    clearFBBusinessList: (state) => {
      state.FBBusinessList = initialState.FBBusinessList;
    },
    clearFBPageList: (state) => {
      state.FBPageList = initialState.FBPageList;
    },
    clearLeadFormList: (state) => {
      state.LeadFormList = initialState.LeadFormList;
    },
    clearFormFieldsList: (state) => {
      state.FormFieldsList = initialState.FormFieldsList;
    },
    clearLeadIntegrationDetails: (state) => {
      state.LeadIntegrationDetails = initialState.LeadIntegrationDetails;
    },
    clearStudentLeadList: (state) => {
      state.StudentLeadList = initialState.StudentLeadList;
    },
  },
  extraReducers: (builder) => {
    // Fetch Facebook Business Portfolios
    builder.addCase(getAllFacebookBusinesses.pending, (state) => {
      state.businessLoading = true;
    });
    builder.addCase(
      getAllFacebookBusinesses.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.businessLoading = false;
        state.FBBusinessList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllFacebookBusinesses.rejected, (state, action) => {
      state.businessLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.FBBusinessList = [];
    });

    // Fetch Pages by Business ID
    builder.addCase(getPagesByBusinessID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getPagesByBusinessID.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.FBPageList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getPagesByBusinessID.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.FBPageList = [];
    });

    // Fetch Facebook Page List (fallback / direct)
    builder.addCase(getAllFacebookPages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAllFacebookPages.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.FBPageList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllFacebookPages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.FBPageList = [];
    });

    // Fetch Facebook Lead Form List By Page ID
    builder.addCase(getLeadFormByPageID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getLeadFormByPageID.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LeadFormList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getLeadFormByPageID.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadFormList = [];
    });

    // Fetch Facebook Fields from Form ID
    builder.addCase(getFormFieldsByFromID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getFormFieldsByFromID.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.FormFieldsList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getFormFieldsByFromID.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.FormFieldsList = [];
    });

    // Integration Section
    builder.addCase(getAllIntegrations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAllIntegrations.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LeadIntegrationList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllIntegrations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadIntegrationList = [];
    });

    builder.addCase(createIntegration.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createIntegration.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createIntegration.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(updateIntegration.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateIntegration.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateIntegration.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(getIntegrationByID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getIntegrationByID.fulfilled,
      (state, action: PayloadAction<ILeadIntegrationDetails>) => {
        state.loading = false;
        state.LeadIntegrationDetails = action.payload;
      }
    );
    builder.addCase(getIntegrationByID.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
    });

    builder.addCase(deleteIntegration.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteIntegration.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteIntegration.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(deleteFBLead.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteFBLead.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteFBLead.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(disConnectFacebook.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(disConnectFacebook.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(disConnectFacebook.rejected, (state) => {
      state.loading = false;
      state.error = "Something went wrong";
      ErrorToast(state.error);
    });

    builder.addCase(getAllStudentLeads.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAllStudentLeads.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.StudentLeadList = action.payload;
        state.error = "";
      }
    );
    builder.addCase(getAllStudentLeads.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.StudentLeadList = [];
    });

    builder.addCase(updatePageSubscription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePageSubscription.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updatePageSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export default integrationSlice.reducer;
export const {
  setIntegrationID,
  clearIntegrationID,
  setFBLeadID,
  clearFBLeadID,
  clearFBBusinessList,
  clearFBPageList,
  clearLeadFormList,
  clearLeadIntegrationDetails,
  clearStudentLeadList,
} = integrationSlice.actions;
